/*
 * Figma `126:361` — SA3-10 · LINE OA Platform.
 *
 * `TableTemplate`: every LINE OA connection on the platform — JamTick's own and each organizer's —
 * with the one you opened in a drawer.
 *
 * This is the same subject as the organizer's LINE OA screen and deliberately not the same screen.
 * There it is one connection and the question is "does mine work". Here it is all of them and the
 * question is "whose is broken", so the registry leads with health per row and the credential
 * lifecycle is the drawer's job.
 *
 * `secretsConfigured` remains the entire vocabulary for a secret. There is no endpoint that returns
 * one and there never will be, so nothing here offers a reveal — the rotation field writes, and the
 * badge says configured or not.
 */
import { useState } from 'react';
import {
  Alert, Button, Card, ConfirmDialog, CredentialReveal, Drawer, FilterChips, KpiCard,
  StatusBadge, TableTemplate, TextField,
} from '../..';
import type { BadgeTone, Column } from '../..';
import type { Destination } from '../../destinations';
import type { LineOaConnectionDto, LineOaStatusDto } from '../../../api';
import { useLineOaPlatform } from './useLineOaPlatform';

const STATUS_LABEL: Record<LineOaStatusDto, string> = {
  draft: 'ร่าง', verification_pending: 'รอตรวจสอบ', active: 'ใช้งานอยู่',
  verification_failed: 'ตรวจไม่ผ่าน', disabled: 'ปิดอยู่', credential_invalid: 'credential เสีย',
};
const STATUS_TONE: Record<LineOaStatusDto, BadgeTone> = {
  draft: 'neutral', verification_pending: 'warning', active: 'success',
  verification_failed: 'danger', disabled: 'neutral', credential_invalid: 'danger',
};

export interface LineOaPlatformState {
  connections: LineOaConnectionDto[];
  environment: string;
  loading: boolean;
  loadError: string;
  error: string;
  busy: string;
  verify: (id: string) => Promise<void>;
  setEnabled: (id: string, enabled: boolean) => Promise<void>;
  rotate: (id: string, secrets: { messagingChannelSecret?: string; messagingAccessToken?: string }) => Promise<void>;
  reload: () => Promise<void>;
}

export function LineOaPlatformScreen({ destination, state }: {
  destination: Destination;
  state: LineOaPlatformState;
}) {
  const { connections, environment, loading, loadError, error, busy, verify, setEnabled, rotate, reload } = state;
  const [scope, setScope] = useState<'all' | 'platform' | 'event' | 'organization'>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const [confirmDisable, setConfirmDisable] = useState<LineOaConnectionDto | null>(null);
  const [secret, setSecret] = useState('');
  const [accessToken, setAccessToken] = useState('');

  const open = openId ? connections.find((c) => c.id === openId) ?? null : null;
  const rows = connections.filter((c) => scope === 'all' || c.ownerScope === scope);
  const broken = connections.filter((c) => c.status === 'verification_failed' || c.status === 'credential_invalid').length;

  const columns: Column<LineOaConnectionDto>[] = [
    {
      key: 'name',
      header: 'บัญชี',
      cell: (c) => (
        <span className="jt-cell-stack">
          <span>{c.displayName}</span>
          <span className="jt-cell-sub jt-mono">{c.basicId ?? '—'}</span>
        </span>
      ),
    },
    {
      key: 'scope',
      header: 'ของใคร',
      width: '160px',
      cell: (c) => (
        <StatusBadge tone={c.ownerScope === 'platform' ? 'accent' : 'neutral'}>
          {c.ownerScope === 'platform' ? 'ของ JamTick' : c.ownerScope === 'event' ? 'ของอีเวนต์' : 'ขององค์กร'}
        </StatusBadge>
      ),
    },
    {
      key: 'capability',
      header: 'ความสามารถ',
      width: '170px',
      cell: (c) => (c.capability === 'messaging' ? 'ส่งข้อความได้' : 'ปุ่มเพิ่มเพื่อนเท่านั้น'),
    },
    {
      key: 'status',
      header: 'สถานะ',
      width: '210px',
      cell: (c) => (
        <span className="jt-cell-stack">
          <StatusBadge tone={STATUS_TONE[c.status]}>{STATUS_LABEL[c.status]}</StatusBadge>
          {c.lastVerificationError && <span className="jt-cell-sub">{c.lastVerificationError}</span>}
        </span>
      ),
    },
    {
      key: 'verified',
      header: 'ตรวจล่าสุด',
      width: '190px',
      cell: (c) => (c.lastVerifiedAt ? new Date(c.lastVerifiedAt).toLocaleString('th-TH') : 'ยังไม่เคยตรวจ'),
    },
  ];

  return (
    <TableTemplate
      gate={{ destination, loading, error: loadError || null, onRetry: reload }}
      kpis={[
        <KpiCard key="all" label="บัญชีที่เชื่อมไว้" value={connections.length.toLocaleString('th-TH')} meta={`environment ${environment || '—'}`} />,
        <KpiCard key="live" label="ใช้งานอยู่" value={connections.filter((c) => c.status === 'active').length.toLocaleString('th-TH')} />,
        <KpiCard key="broken" label="ที่ใช้งานไม่ได้" value={broken.toLocaleString('th-TH')} tone={broken > 0 ? 'danger' : undefined} />,
      ]}
      summary={error ? <Alert tone="danger" title="ทำรายการไม่สำเร็จ">{error}</Alert> : undefined}
      toolbar={
        <FilterChips
          chips={[
            { key: 'all', label: 'ทั้งหมด', count: connections.length },
            { key: 'platform', label: 'ของ JamTick', count: connections.filter((c) => c.ownerScope === 'platform').length },
            { key: 'event', label: 'ของอีเวนต์', count: connections.filter((c) => c.ownerScope === 'event').length },
            { key: 'organization', label: 'ขององค์กร', count: connections.filter((c) => c.ownerScope === 'organization').length },
          ]}
          active={scope}
          onSelect={(k) => setScope(k as typeof scope)}
          label="ขอบเขตของบัญชี"
        />
      }
      table={{
        label: 'LINE OA ทั้งแพลตฟอร์ม',
        columns,
        rows,
        rowKey: (c) => c.id,
        filtered: scope !== 'all',
        onClearFilters: () => setScope('all'),
        emptyTitle: 'ยังไม่มีบัญชี LINE OA',
        onRowClick: (c) => { setOpenId(c.id); setSecret(''); setAccessToken(''); },
        activeRowKey: openId,
        card: (c) => (
          <span className="jt-cell-stack">
            <span>{c.displayName}</span>
            <span className="jt-cell-sub jt-mono">{c.basicId ?? '—'}</span>
            <StatusBadge tone={STATUS_TONE[c.status]}>{STATUS_LABEL[c.status]}</StatusBadge>
          </span>
        ),
      }}
      drawer={
        <>
          <Drawer
            open={!!open}
            onClose={() => setOpenId(null)}
            title={open?.displayName ?? ''}
            footer={open && (
              <>
                <Button variant="secondary" disabled={busy === open.id} onClick={() => void verify(open.id)}>
                  ตรวจสอบการเชื่อมต่อ
                </Button>
                <Button
                  variant={open.status === 'disabled' ? 'primary' : 'destructive'}
                  disabled={busy === open.id}
                  onClick={() => (open.status === 'disabled' ? void setEnabled(open.id, true) : setConfirmDisable(open))}
                >
                  {open.status === 'disabled' ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                </Button>
              </>
            )}
          >
            {open && (
              <>
                {open.lastVerificationError && (
                  <Alert tone="danger" title="ผลตรวจล่าสุดไม่ผ่าน">{open.lastVerificationError}</Alert>
                )}
                {open.missingFields.length > 0 && (
                  <Alert tone="warning" title="ยังกรอกไม่ครบ">{open.missingFields.join(', ')}</Alert>
                )}

                <Card title="ข้อมูลการเชื่อมต่อ">
                  <ul className="jt-order-lines">
                    <li><span>Basic ID</span><span className="jt-mono">{open.basicId ?? '—'}</span></li>
                    <li><span>Messaging channel</span><span className="jt-mono">{open.messagingChannelId ?? '—'}</span></li>
                    <li><span>Login channel</span><span className="jt-mono">{open.loginChannelId ?? '—'}</span></li>
                    <li><span>LIFF</span><span className="jt-mono">{open.liffId ?? '—'}</span></li>
                    <li className="jt-order-total"><span>เก็บ secret ที่</span><span>{open.secretSource === 'deployment' ? 'deployment' : 'ฐานข้อมูล'}</span></li>
                  </ul>
                </Card>

                <Card title="Credential" sub="ระบบไม่คืนค่าเดิม — เปลี่ยนได้อย่างเดียวคือใส่ค่าใหม่ทับ">
                  <CredentialReveal
                    label="Channel secret"
                    masked={open.secretsConfigured.messagingSecret ? '•••••••• ตั้งค่าแล้ว' : 'ยังไม่ได้ตั้ง'}
                    status={open.status === 'credential_invalid' ? 'revoked' : 'active'}
                  />
                  <CredentialReveal
                    label="Channel access token"
                    masked={open.secretsConfigured.messagingAccessToken ? '•••••••• ตั้งค่าแล้ว' : 'ยังไม่ได้ตั้ง'}
                    status={open.status === 'credential_invalid' ? 'revoked' : 'active'}
                  />
                  <div className="jt-field-grid">
                    <TextField
                      label="Channel secret ใหม่" type="password" autoComplete="off"
                      value={secret} onChange={(e) => setSecret(e.target.value)}
                    />
                    <TextField
                      label="Access token ใหม่" type="password" autoComplete="off"
                      value={accessToken} onChange={(e) => setAccessToken(e.target.value)}
                    />
                  </div>
                  <div className="jt-inline-actions">
                    <Button
                      size="sm"
                      variant="primary"
                      disabled={busy === open.id || (!secret && !accessToken)}
                      onClick={async () => {
                        await rotate(open.id, {
                          ...(secret ? { messagingChannelSecret: secret } : {}),
                          ...(accessToken ? { messagingAccessToken: accessToken } : {}),
                        });
                        setSecret('');
                        setAccessToken('');
                      }}
                    >
                      บันทึก secret
                    </Button>
                  </div>
                </Card>
              </>
            )}
          </Drawer>

          <ConfirmDialog
            open={!!confirmDisable}
            onClose={() => setConfirmDisable(null)}
            onConfirm={() => {
              const target = confirmDisable;
              setConfirmDisable(null);
              if (target) void setEnabled(target.id, false);
            }}
            title={`ปิดใช้งาน ${confirmDisable?.displayName ?? ''}`}
            confirmLabel="ปิดใช้งาน"
            consequences={
              confirmDisable?.ownerScope === 'platform'
                ? [
                  'นี่คือ OA ของ JamTick เอง — ปิดแล้วการส่งบัตรทาง LINE จะหยุดทั้งแพลตฟอร์ม',
                  'ออร์เดอร์ใหม่ทุกอีเวนต์จะไม่ได้รับบัตรทาง LINE จนกว่าจะเปิดกลับ',
                  'บัตรที่ส่งไปแล้วไม่ถูกเรียกคืน',
                ]
                : [
                  'อีเวนต์ที่ผูกกับบัญชีนี้จะไม่ส่งข้อความผ่าน OA นี้อีก',
                  'ปุ่มเพิ่มเพื่อนบนหน้าสถานะคำสั่งซื้อจะหายไปด้วย',
                  'บัตรที่ส่งไปแล้วไม่ถูกเรียกคืน',
                ]
            }
          />
        </>
      }
    />
  );
}

export function LineOaPlatformDestination({ token, destination }: { token: string; destination: Destination }) {
  const state = useLineOaPlatform(token);
  return <LineOaPlatformScreen destination={destination} state={state} />;
}
